import styled from "styled-components"
import { tint } from "polished"
import ReactMarkdown from "react-markdown"

import { PerkShape } from "~shapes/perk"
import ScrollableStyle from "~components/ScrollableStyle"
import ImagePathNormalizer from "~utils/image-path-normalizer"
import colors from "~constants/colors"

interface Props {
  perk: PerkShape
}

const PerkDetailScreen = ({ perk }: Props) => {
  return (
    <PerkDetailContainer
      style={{ backgroundColor: `#${perk.company?.color || colors.white}` }}
    >
      <PerkDetail>
        <MainSection>
          {!!perk.cover && (
            <PerkCoverContainer>
              <PerkCover src={ImagePathNormalizer(perk.cover)} />
            </PerkCoverContainer>
          )}

          <PerkContent style={{ marginTop: 20 }}>
            <CompanySection style={{ marginBottom: 22 }}>
              <CompanyLogo src={ImagePathNormalizer(perk.logo)} />
              <CompanyNameAndSymbol>
                {perk.company?.name}
                <span>
                  {perk.company?.exchange}: {perk.company?.symbol}
                </span>
              </CompanyNameAndSymbol>
            </CompanySection>

            <Badge style={{ marginBottom: 14 }}>{perk.shortDescription}</Badge>

            <PerkTitle>{perk.name}</PerkTitle>

            <ReactMarkdown>{perk.description}</ReactMarkdown>

            <EligibilityRulesTitle>Eligibility Rules</EligibilityRulesTitle>

            <p>{perk.rules}</p>
          </PerkContent>
        </MainSection>

        <BottomSection>
          <DashedLine color={perk.company?.color} />

          <PerkContent>
            <RedeemButton>Redeem perk</RedeemButton>
          </PerkContent>
        </BottomSection>
      </PerkDetail>
    </PerkDetailContainer>
  )
}

const PerkDetailContainer = styled.div`
  height: 100%;
  padding: 6vh 1.5vh 1.5vh;
`

const PerkDetail = styled.div`
  background-color: white;
  border-radius: 16px;
  color: #5c6268;
  font-size: 13px;
  line-height: 18px;
  display: flex;
  flex-direction: column;
  font-family: "Inter", sans-serif;
  height: 100%;
  margin-bottom: 35px;
  padding-bottom: 20px;
`

const MainSection = styled(ScrollableStyle)`
  height: 100%;
  overflow: auto;
`

const BottomSection = styled.div`
  min-height: 66px;
`

const PerkCoverContainer = styled.figure`
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  margin: 0;
  overflow: hidden;
`

const PerkCover = styled.img`
  height: 114px;
  margin: 0;
  object-fit: cover;
  width: 100%;
`

const PerkContent = styled.div`
  padding: 0 20px;
`

const CompanySection = styled.div`
  align-items: center;
  display: flex;
`

const CompanyLogo = styled.img`
  height: 48px;
  margin: 0 10px 0 0;
  object-fit: contain;
  width: 48px;
`

const CompanyNameAndSymbol = styled.p`
  color: black;
  font-size: 14px;
  margin: 0;

  > span {
    color: #8c8c8c;
    display: block;
    font-size: 10px;
  }
`

const Badge = styled.div`
  background-color: ${tint(0.9, "#8868C9")};
  border-radius: 23px;
  color: #8868c9;
  display: inline-block;
  font-size: 10px;
  line-height: 16px;
  padding: 2px 6px;
`

const PerkTitle = styled.h6`
  color: black;
  font-size: 16px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  line-height: 22px;
`

const EligibilityRulesTitle = styled.h6`
  color: black;
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 10px 0;
`

const DashedLine = styled.div`
  border-bottom: 1px dashed #e0e1e1;
  margin-bottom: 25px;
  position: relative;

  &:after,
  &:before {
    border-radius: 50%;
    background-color: #${props => props.color};
    content: "";
    display: inline-block;
    height: 20px;
    position: absolute;
    top: -10px;
    width: 20px;
  }

  &:after {
    right: -10px;
  }

  &:before {
    left: -10px;
  }
`

const RedeemButton = styled.button`
  background-color: #edeeee;
  border: none;
  border-radius: 8px;
  color: #babec2;
  height: 40px;
  width: 100%;
`

export default PerkDetailScreen
